import { useQueries, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelineStageExecutions, queryPipelines } from '@/features/pipelines/data';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { type PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import { type QualityRange, useQualityDateRange } from '@/features/quality/hooks';
import { buildMetricsChartToolbarState } from '@/features/shared/components/chartPanels/chartPanelAdapters';
import { resolveMetaDateBounds } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import {
  useDailyHistoryPanelPageReset,
  useDailyHistoryPanelState,
} from '@/features/shared/hooks/useDailyHistoryPanelState';
import { resolveWindowDayKeys } from '@/features/shared/utils/chartDateWindow';
import { getChartTitle } from '@/shared/copy/charts';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import {
  queryKeys,
  useIsAuthenticated,
  usePersistedStringArray,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import {
  applyFilteredRunSummariesToPipelines,
  fetchFilteredPipelineRunSummaries,
  groupPipelineRunSummariesByPipelineId,
} from './pipelineFilteredRows';
import { buildPipelineHistoryRows } from './pipelineHistoryRows';
import { buildPipelineEntityFilters } from './pipelinePageFilters';
import {
  shouldLoadPipelineOptionsQuery,
  shouldLoadPipelineSecondaryQueries,
} from './pipelinePageQueryGates';
import { shouldUseWindowedPipelineStatusFiltering } from './pipelineStatusScope';
import { usePipelinesOverviewModel } from './usePipelinesOverviewModel';
import {
  fetchWindowedStatusPipelineIds,
  usePipelinesPageFilterOptionCounts,
} from './usePipelinesPageFilterOptionCounts';
import type { ExecutionStatusKey } from '@/features/metrics/metricsHelpers';
import type { PipelinesTablePanelProps } from '@/features/pipelines/components/PipelinesTablePanel';
import type { StackedMetricsStatusChartSectionProps } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type {
  BuildStatus,
  PipelineListItemDto,
  PipelineRunSummaryItemDto,
  PipelineStageExecutionListItemDto,
} from '@flowqa/shared';

const toLatestBuildStatuses = (statuses: PipelineStatusKey[]): BuildStatus[] =>
  Array.from(
    new Set(
      statuses.flatMap((status): BuildStatus[] => {
        if (status === 'success') return ['completed'];
        if (status === 'running') return ['running'];
        if (status === 'queued') return ['queued'];
        if (status === 'none') return [];
        return ['failed'];
      }),
    ),
  );

type UsePipelinesPageViewModelResult = {
  metricSectionProps: StackedMetricsStatusChartSectionProps<PipelineStatusKey>;
  tablePanelProps: PipelinesTablePanelProps;
  dailyHistoryRows: EntityDailyHistoryRow[];
  dailyHistoryDayKeys: string[];
  dailyHistoryLoading: boolean;
  dailyHistoryHasError: boolean;
  dailyHistoryNoRowsText: string;
  dailyHistoryDateRangeLabel: string | null;
  expandedRowIds: Set<string>;
  dailyHistoryPage: number;
  dailyHistoryPageSize: number;
  dailyHistorySortDirection: 'asc' | 'desc';
  dailyHistorySortKey: DailyHistorySortKey;
  dailyHistoryTotalCount: number;
  setDailyHistoryPage: (nextPage: number) => void;
  setDailyHistoryPageSize: (nextPageSize: number) => void;
  setDailyHistorySortDirection: (nextSortDirection: 'asc' | 'desc') => void;
  setDailyHistorySortKey: (nextSortKey: DailyHistorySortKey) => void;
  setExpandedRowIds: (nextExpandedRowIds: Set<string>) => void;
};

type PipelineHistoryQueryResult = {
  items: PipelineListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  filteredRunSummariesByPipelineId: ReadonlyMap<string, readonly PipelineRunSummaryItemDto[]>;
};

export const usePipelinesPageViewModel = (): UsePipelinesPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState<QualityRange>('7d');
  const [comparisonActivated, setComparisonActivated] = useState(false);
  const [activeTab, setActiveTab] = useState<'pipelines' | 'jobs'>('pipelines');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<PipelineStatusKey[]>([]);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:pipelines');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const {
    page: dailyHistoryPage,
    pageSize: dailyHistoryPageSize,
    sortKey: dailyHistorySortKey,
    sortDirection: dailyHistorySortDirection,
    isTransitionLoading: isHistoryTransitionLoading,
    setPage: setDailyHistoryPage,
    setPageSize: setDailyHistoryPageSize,
    setSortKey: setDailyHistorySortKey,
    setSortDirection: setDailyHistorySortDirection,
  } = useDailyHistoryPanelState({
    defaultSortKey: 'name',
  });
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const showDelta =
    comparisonActivated && !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const effectiveProjectId =
    selectedProjectId ?? (selectedProjects.length === 1 ? (selectedProjects[0] ?? null) : null);
  const showProjectFilter = selectedProjectId === null;
  const projectsQuery = useProjectsQuery({ enabled: showProjectFilter });
  const projectFilterOptions = useMemo(
    () =>
      (projectsQuery.data?.items ?? []).map((project) => ({
        value: project.id,
        label: project.name,
      })),
    [projectsQuery.data?.items],
  );
  const { tagOptions } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated,
  });
  const { thresholds } = useProjectMetricThresholds(effectiveProjectId);
  const selectedChartStatuses = useMemo<ExecutionStatusKey[]>(() => {
    const mapped = selectedStatuses
      .map((status): ExecutionStatusKey | null => {
        if (status === 'success') return 'passed';
        if (status === 'warning' || status === 'failed' || status === 'canceled') {
          return 'failed';
        }
        return null;
      })
      .filter((status): status is ExecutionStatusKey => status !== null);
    return Array.from(new Set(mapped));
  }, [selectedStatuses]);
  const hasUnsupportedChartStatusSelection =
    selectedStatuses.length > 0 && selectedChartStatuses.length === 0;
  const selectedBuildStatuses = useMemo<BuildStatus[]>(
    () => toLatestBuildStatuses(selectedStatuses),
    [selectedStatuses],
  );
  const useWindowedStatusFiltering = shouldUseWindowedPipelineStatusFiltering(
    range,
    selectedStatuses,
  );
  const {
    statusFilterOptions,
    pipelinesNoRowsText,
    pipelineMetricCards,
    modelOverride,
    currentSummaryLoading,
    hasCurrentSummary,
  } = usePipelinesOverviewModel({
    isAuthenticated,
    selectedProjectId: effectiveProjectId,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedPipelineIds,
    range,
    rangeStartIso,
    rangeEndIso,
    selectedStartDay,
    selectedEndDay,
    previousRangeStartIso,
    previousRangeEndIso,
    debouncedSearchValue,
    selectedStatuses,
    selectedTags,
    hasUnsupportedChartStatusSelection,
    thresholds: thresholds.pipelines,
    showDelta,
  });
  const secondaryPipelineQueriesEnabled = shouldLoadPipelineSecondaryQueries({
    currentSummaryLoading,
    hasCurrentSummary,
    isAuthenticated,
  });
  const pickerStartDay = selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null;
  const pickerEndDay = selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null;

  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setComparisonActivated(nextDay !== null);
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setComparisonActivated(nextDay !== null);
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const resetRangeDates = useCallback((next: DateQuickFilter) => {
    setRange(next);
    setComparisonActivated(next !== 'latest');
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const chartToolbarQuickFilter: DateQuickFilter =
    range === '14d' || range === '30d' || range === '90d' || range === 'latest' ? range : '7d';
  const forcedLoadState = searchParams.get('loadState');
  const showForcedLoadingState = forcedLoadState === 'loading';
  const statusScopedPipelineIdsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipelines-status-scope',
      selectedOrganisationId ?? null,
      effectiveProjectId ?? null,
      debouncedSearchValue,
      [...selectedPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedTags].sort((left, right) => left.localeCompare(right)).join(','),
      [...selectedStatuses].sort((left, right) => left.localeCompare(right)).join(','),
      selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
      selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
      useWindowedStatusFiltering ? 'windowed' : 'latest',
    ),
    enabled: isAuthenticated && useWindowedStatusFiltering,
    queryFn: async () =>
      await fetchWindowedStatusPipelineIds({
        effectiveProjectId,
        selectedOrganisationId: selectedOrganisationId ?? null,
        debouncedSearchValue,
        selectedPipelineIds,
        selectedStatuses,
        selectedTags,
        startDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        endDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
      }),
  });
  const windowedStatusPipelineIds =
    useWindowedStatusFiltering && !statusScopedPipelineIdsQuery.isError
      ? (statusScopedPipelineIdsQuery.data ?? [])
      : null;
  const dailyHistoryFiltersKey = useMemo(
    () =>
      JSON.stringify({
        projectId: effectiveProjectId,
        organisationId: selectedOrganisationId,
        search: debouncedSearchValue,
        selectedBuildStatuses,
        selectedPipelineIds,
        dailyHistoryPage,
        dailyHistoryPageSize,
        dailyHistorySortDirection,
        dailyHistorySortKey,
        selectedTags,
        startDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        endDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
        useWindowedStatusFiltering,
        windowedStatusPipelineIds:
          windowedStatusPipelineIds === null
            ? null
            : [...windowedStatusPipelineIds].sort((left, right) => left.localeCompare(right)),
      }),
    [
      debouncedSearchValue,
      dailyHistoryPage,
      dailyHistoryPageSize,
      dailyHistorySortDirection,
      dailyHistorySortKey,
      rangeEndIso,
      rangeStartIso,
      selectedBuildStatuses,
      selectedPipelineIds,
      selectedEndDay,
      selectedOrganisationId,
      effectiveProjectId,
      selectedStartDay,
      selectedTags,
      useWindowedStatusFiltering,
      windowedStatusPipelineIds,
    ],
  );
  const dailyHistoryQuery = useQuery({
    queryKey: queryKeys.dataGrid('pipelines-daily-history', dailyHistoryFiltersKey),
    enabled: secondaryPipelineQueriesEnabled,
    queryFn: async (): Promise<PipelineHistoryQueryResult> => {
      if (windowedStatusPipelineIds !== null && windowedStatusPipelineIds.length === 0) {
        return {
          items: [],
          total: 0,
          page: dailyHistoryPage + 1,
          pageSize: dailyHistoryPageSize,
          filteredRunSummariesByPipelineId: new Map(),
        };
      }

      const result = await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
        page: dailyHistoryPage + 1,
        pageSize: dailyHistoryPageSize,
        sorts: [{ field: dailyHistorySortKey, direction: dailyHistorySortDirection }],
        filters: mergeFilters(
          buildContainsFilter('name', debouncedSearchValue),
          windowedStatusPipelineIds === null
            ? selectedStatuses.length > 0
              ? buildInFilterWithNull(
                  'latestRunStatus',
                  selectedStatuses.includes('none')
                    ? [null, ...selectedBuildStatuses]
                    : selectedBuildStatuses,
                )
              : []
            : buildPipelineEntityFilters(windowedStatusPipelineIds),
          buildPipelineEntityFilters(selectedPipelineIds),
          buildInFilter('tags', selectedTags),
        ),
        metricsStartDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? undefined,
        metricsEndDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? undefined,
        include: {
          latestRun: true,
          runSummaries: true,
          dailyHistory: true,
        },
      });

      if (!useWindowedStatusFiltering || selectedStatuses.length === 0) {
        return {
          ...result,
          filteredRunSummariesByPipelineId: new Map(),
        };
      }

      const filteredRunSummaries = await fetchFilteredPipelineRunSummaries({
        selectedProjectId: effectiveProjectId,
        selectedOrganisationId: selectedOrganisationId ?? null,
        pipelineIds: result.items.map((pipeline) => pipeline.id),
        statuses: selectedStatuses,
        selectedTags,
        startDay: selectedStartDay ?? rangeStartIso?.slice(0, 10) ?? null,
        endDay: selectedEndDay ?? rangeEndIso?.slice(0, 10) ?? null,
      });
      const filteredRunSummariesByPipelineId =
        groupPipelineRunSummariesByPipelineId(filteredRunSummaries);

      return {
        ...result,
        items: applyFilteredRunSummariesToPipelines(result.items, filteredRunSummariesByPipelineId),
        filteredRunSummariesByPipelineId,
      };
    },
  });
  const pipelineOptionsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'pipeline-options',
      selectedOrganisationId ?? null,
      effectiveProjectId ?? null,
    ),
    enabled: shouldLoadPipelineOptionsQuery({
      dailyHistorySettled: dailyHistoryQuery.isFetched || dailyHistoryQuery.isError,
      secondaryQueriesEnabled: secondaryPipelineQueriesEnabled,
    }),
    queryFn: async () =>
      await fetchAllQueryPages(
        async ({ page, pageSize }) =>
          await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
            page,
            pageSize,
            sorts: [{ field: 'name', direction: 'asc' }],
            filters: [],
          }),
      ),
  });
  const pipelineOptions = useMemo(
    () =>
      (pipelineOptionsQuery.data?.items ?? [])
        .map((pipeline) => ({
          value: pipeline.id,
          label: pipeline.name,
          projectId: pipeline.projectId,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [pipelineOptionsQuery.data?.items],
  );
  const latestRunDateBounds = useMemo(
    () =>
      resolveMetaDateBounds(
        dailyHistoryQuery.data ?? null,
        (pipeline) => pipeline.latestRun?.timestamp,
      ),
    [dailyHistoryQuery.data],
  );
  const effectiveHistoryStartDay =
    selectedStartDay ??
    (isLatestMode
      ? (latestRunDateBounds.from?.format('YYYY-MM-DD') ?? null)
      : (rangeStartIso?.slice(0, 10) ?? null));
  const effectiveHistoryEndDay =
    selectedEndDay ??
    (isLatestMode
      ? (latestRunDateBounds.to?.format('YYYY-MM-DD') ?? null)
      : (rangeEndIso?.slice(0, 10) ?? null));
  const visiblePipelineIds = useMemo(
    () =>
      (dailyHistoryQuery.data?.items ?? [])
        .map((pipeline) => pipeline.id)
        .sort((left, right) => left.localeCompare(right)),
    [dailyHistoryQuery.data?.items],
  );
  const { countedPipelineOptions, countedTagOptions } = usePipelinesPageFilterOptionCounts({
    debouncedSearchValue,
    effectiveProjectId,
    enabled: secondaryPipelineQueriesEnabled,
    isAuthenticated,
    pipelineOptions,
    rangeEndIso,
    rangeStartIso,
    selectedBuildStatuses,
    selectedEndDay,
    selectedOrganisationId: selectedOrganisationId ?? null,
    selectedPipelineIds,
    selectedStartDay,
    selectedStatuses,
    selectedTags,
    tagOptions,
    useWindowedStatusFiltering,
  });
  const visibleExpandedPipelineIds = useMemo(
    () =>
      [...expandedRowIds]
        .filter((pipelineId) => visiblePipelineIds.includes(pipelineId))
        .sort((left, right) => left.localeCompare(right)),
    [expandedRowIds, visiblePipelineIds],
  );
  const expandedPipelineStageExecutionQueries = useQueries({
    queries: visibleExpandedPipelineIds.map((pipelineId) => ({
      queryKey: queryKeys.dataGrid(
        'pipelines-history',
        'stage-executions',
        JSON.stringify({
          effectiveHistoryEndDay,
          effectiveHistoryStartDay,
          pipelineId,
          selectedOrganisationId,
          effectiveProjectId,
        }),
      ),
      queryFn: async () =>
        await fetchAllQueryPages(
          async ({ page, pageSize }) =>
            await queryPipelineStageExecutions(
              effectiveProjectId,
              selectedOrganisationId ?? undefined,
              {
                page,
                pageSize,
                sorts: [
                  { field: 'startedAt', direction: 'desc' },
                  { field: 'createdAt', direction: 'desc' },
                ],
                filters: mergeFilters(
                  buildInFilter('pipelineId', [pipelineId]),
                  buildDateRangeFilters(
                    'timestamp',
                    effectiveHistoryStartDay ? `${effectiveHistoryStartDay}T00:00:00.000Z` : null,
                    effectiveHistoryEndDay ? `${effectiveHistoryEndDay}T23:59:59.999Z` : null,
                  ),
                ),
              },
            ),
        ),
      enabled:
        isAuthenticated && effectiveHistoryStartDay !== null && effectiveHistoryEndDay !== null,
    })),
  });
  const stageExecutionsByPipelineId = useMemo(() => {
    const next = new Map<string, PipelineStageExecutionListItemDto[]>();
    visibleExpandedPipelineIds.forEach((pipelineId, index) => {
      const items = expandedPipelineStageExecutionQueries[index]?.data?.items ?? [];
      const filteredRunSummaries =
        dailyHistoryQuery.data?.filteredRunSummariesByPipelineId.get(pipelineId);

      if (
        !useWindowedStatusFiltering ||
        !filteredRunSummaries ||
        filteredRunSummaries.length === 0
      ) {
        next.set(pipelineId, items);
        return;
      }

      const allowedBuildKeys = new Set(
        filteredRunSummaries
          .map((runSummary) => runSummary.buildKey)
          .filter(
            (buildKey): buildKey is string => typeof buildKey === 'string' && buildKey.length > 0,
          ),
      );

      next.set(
        pipelineId,
        items.filter((execution) =>
          execution.buildKey === null || execution.buildKey === undefined
            ? false
            : allowedBuildKeys.has(execution.buildKey),
        ),
      );
    });
    return next;
  }, [
    dailyHistoryQuery.data?.filteredRunSummariesByPipelineId,
    expandedPipelineStageExecutionQueries,
    useWindowedStatusFiltering,
    visibleExpandedPipelineIds,
  ]);
  const expandedPipelineIdsLoading = useMemo(() => {
    const next = new Set<string>();
    visibleExpandedPipelineIds.forEach((pipelineId, index) => {
      if (expandedPipelineStageExecutionQueries[index]?.isFetching) {
        next.add(pipelineId);
      }
    });
    return next;
  }, [expandedPipelineStageExecutionQueries, visibleExpandedPipelineIds]);
  const toolbarState = useMemo(
    () => ({
      ...buildMetricsChartToolbarState({
        search: debouncedSearchValue,
        setSearch: setDebouncedSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: pipelinesNoRowsText,
        dateQuickFilter: chartToolbarQuickFilter,
        onDateQuickFilterChange: resetRangeDates,
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        latestQueryStartDay: latestRunDateBounds.from?.format('YYYY-MM-DD') ?? null,
        latestQueryEndDay: latestRunDateBounds.to?.format('YYYY-MM-DD') ?? null,
        onFromDateChange: handleStartDayChange,
        onToDateChange: handleEndDayChange,
      }),
      selectedEntities: selectedPipelineIds,
      setSelectedEntities: setSelectedPipelineIds,
      entityOptions: countedPipelineOptions,
      entityLabel: 'Pipeline',
      selectedProjects,
      setSelectedProjects,
      projectOptions: showProjectFilter ? projectFilterOptions : undefined,
    }),
    [
      pipelinesNoRowsText,
      chartToolbarQuickFilter,
      countedPipelineOptions,
      debouncedSearchValue,
      handleEndDayChange,
      handleStartDayChange,
      latestRunDateBounds.from,
      latestRunDateBounds.to,
      projectFilterOptions,
      resetRangeDates,
      selectedEndDay,
      selectedProjects,
      selectedPipelineIds,
      selectedStartDay,
      selectedStatuses,
      selectedTags,
      setSelectedTags,
      showProjectFilter,
    ],
  );
  const dailyHistoryPageResetKey = useMemo(
    () =>
      JSON.stringify({
        debouncedSearchValue,
        rangeEndIso,
        rangeStartIso,
        selectedBuildStatuses,
        selectedPipelineIds,
        selectedEndDay,
        selectedOrganisationId,
        effectiveProjectId,
        selectedStartDay,
        selectedTags,
      }),
    [
      debouncedSearchValue,
      rangeEndIso,
      rangeStartIso,
      selectedBuildStatuses,
      selectedPipelineIds,
      selectedEndDay,
      selectedOrganisationId,
      effectiveProjectId,
      selectedStartDay,
      selectedTags,
    ],
  );
  useDailyHistoryPanelPageReset({
    resetKey: dailyHistoryPageResetKey,
    setPage: setDailyHistoryPage,
  });
  const dailyHistoryRows = useMemo(
    () =>
      buildPipelineHistoryRows(
        dailyHistoryQuery.data?.items,
        stageExecutionsByPipelineId,
        new Set(visibleExpandedPipelineIds),
        expandedPipelineIdsLoading,
        isLatestMode,
      ),
    [
      dailyHistoryQuery.data?.items,
      expandedPipelineIdsLoading,
      isLatestMode,
      stageExecutionsByPipelineId,
      visibleExpandedPipelineIds,
    ],
  );
  const dailyHistoryDayKeys = useMemo(
    () =>
      resolveWindowDayKeys({
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        fallbackStartDay: effectiveHistoryStartDay,
        fallbackEndDay: effectiveHistoryEndDay,
        descending: true,
      }),
    [effectiveHistoryEndDay, effectiveHistoryStartDay, selectedEndDay, selectedStartDay],
  );
  const dailyHistoryDateRangeLabel = useMemo(() => {
    if (!pickerStartDay || !pickerEndDay) return null;
    return `${dayjs(pickerStartDay).format('MMM D, YYYY')} - ${dayjs(pickerEndDay).format('MMM D, YYYY')}`;
  }, [pickerEndDay, pickerStartDay]);
  return {
    metricSectionProps: {
      title: getChartTitle('statusTimeline'),
      searchAriaLabel: 'Search pipelines',
      noRowsBaseText: getEmptyStateMessage('entity.noneFound', { entity: 'pipelineRun' }),
      scope: 'pipeline',
      projectId: effectiveProjectId,
      availableTags: countedTagOptions,
      statusFilterLabel: getChartTitle('status'),
      metricCards: pipelineMetricCards,
      statusOptions: statusFilterOptions,
      toolbarState,
      modelOverride,
      loading: showForcedLoadingState || currentSummaryLoading,
      suppressAnimation: false,
      includeLatestQuickFilter: true,
    },
    tablePanelProps: {
      activeTab,
      onActiveTabChange: setActiveTab,
      selectedProjectId: effectiveProjectId ?? null,
      selectedOrganisationId: selectedOrganisationId ?? null,
      projectFilterProjects: selectedProjects,
      onProjectFilterProjectsChange: setSelectedProjects,
      projectFilterOptions: showProjectFilter ? projectFilterOptions : undefined,
      deltasEnabled: true,
      pipelineSearchValue: debouncedSearchValue,
      onPipelineSearchChange: setDebouncedSearchValue,
      pipelineFilterStatuses: selectedStatuses,
      onPipelineFilterStatusesChange: setSelectedStatuses,
      pipelineFilterPipelines: selectedPipelineIds,
      onPipelineFilterPipelinesChange: setSelectedPipelineIds,
      pipelineOptions: countedPipelineOptions,
      pipelineFilterTags: selectedTags,
      onPipelineFilterTagsChange: setSelectedTags,
      pipelineDateQuickFilter: chartToolbarQuickFilter,
      onPipelineDateQuickFilterChange: resetRangeDates,
      pipelineFromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      pipelineToDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      latestQueryStartDay: latestRunDateBounds.from?.format('YYYY-MM-DD') ?? null,
      latestQueryEndDay: latestRunDateBounds.to?.format('YYYY-MM-DD') ?? null,
      onPipelineFromDateChange: (next) => {
        handleStartDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
      onPipelineToDateChange: (next) => {
        handleEndDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
      tagOptions: countedTagOptions,
      windowedStatusPipelineIds,
    },
    dailyHistoryRows,
    dailyHistoryDayKeys,
    dailyHistoryLoading:
      showForcedLoadingState ||
      (isAuthenticated && !secondaryPipelineQueriesEnabled) ||
      isHistoryTransitionLoading ||
      (dailyHistoryQuery.data === undefined && dailyHistoryQuery.isFetching) ||
      statusScopedPipelineIdsQuery.isFetching,
    dailyHistoryHasError: dailyHistoryQuery.isError || statusScopedPipelineIdsQuery.isError,
    dailyHistoryNoRowsText: pipelinesNoRowsText,
    dailyHistoryDateRangeLabel,
    expandedRowIds,
    dailyHistoryPage,
    dailyHistoryPageSize,
    dailyHistorySortDirection,
    dailyHistorySortKey,
    dailyHistoryTotalCount: dailyHistoryQuery.data?.total ?? 0,
    setDailyHistoryPage,
    setDailyHistoryPageSize,
    setDailyHistorySortDirection,
    setDailyHistorySortKey,
    setExpandedRowIds,
  };
};
